import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloud, Trash2, Layout, Image as ImageIcon, CheckCircle } from 'lucide-react';
import './AdminGallery.css';

const API_URL = 'https://api.sohanihandicrafts.com:5010';

const AdminGallery = () => {
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Form State
    const [altText, setAltText] = useState('');
    const [layout, setLayout] = useState('regular');
    const [galleryImage, setGalleryImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);

    const fetchImages = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/gallery/all`);
            setImages(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchImages(); }, []);

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGalleryImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!galleryImage) return Swal.fire('Warning', 'Please select an image', 'warning');

        const formData = new FormData();
        formData.append('alt', altText || 'Handicraft Item');
        formData.append('layout', layout);
        formData.append('galleryImage', galleryImage);

        try {
            await axios.post(`${API_URL}/api/gallery/add`, formData);
            Swal.fire({ icon: 'success', title: 'Image Added to Gallery', showConfirmButton: false, timer: 1500 });
            // Reset
            setAltText(''); setLayout('regular'); setGalleryImage(null); setPreview(null);
            fetchImages();
        } catch (err) {
            Swal.fire('Error', 'Failed to upload image', 'error');
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Remove from Gallery?',
            text: "This image will be permanently deleted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${API_URL}/api/gallery/delete/${id}`);
                    fetchImages();
                    Swal.fire('Deleted!', 'Image has been removed.', 'success');
                } catch (err) {
                    Swal.fire('Error', 'Could not delete image', 'error');
                }
            }
        });
    };

    return (
        <div className="adm-gal-wrapper">
            <div className="adm-gal-header">
                <h1>Gallery Master</h1>
                <p>Manage the visual showcase of your artisan portfolio</p>
            </div>

            <div className="adm-gal-layout">
                
                {/* --- UPLOAD SECTION --- */}
                <div className="adm-gal-card upload-card">
                    <div className="adm-gal-card-head">
                        <ImageIcon size={20} color="#C5A059" />
                        <h3>Add New Work</h3>
                    </div>
                    <form onSubmit={handleSubmit} className="adm-gal-form">
                        <div className="adm-gal-group">
                            <label>Image Description (Alt Text)</label>
                            <input 
                                type="text" 
                                value={altText} 
                                onChange={(e) => setAltText(e.target.value)} 
                                placeholder="e.g. Hand-carved teak wall art"
                            />
                        </div>

                        <div className="adm-gal-group">
                            <label>Grid Presentation</label>
                            <div className="adm-gal-layout-options">
                                {['regular', 'wide', 'tall'].map((opt) => (
                                    <button 
                                        key={opt}
                                        type="button"
                                        className={`layout-btn ${layout === opt ? 'active' : ''}`}
                                        onClick={() => setLayout(opt)}
                                    >
                                        <Layout size={16} /> {opt}
                                    </button>
                                ))}
                            </div>
                            <p className="layout-hint">Choose how the image fits in the masonry grid.</p>
                        </div>

                        <div className="adm-gal-dropzone" onClick={() => fileInputRef.current.click()}>
                            <input type="file" hidden ref={fileInputRef} onChange={handleFile} accept="image/*" />
                            {preview ? (
                                <img src={preview} alt="preview" className="img-preview" />
                            ) : (
                                <div className="dropzone-inner">
                                    <UploadCloud size={40} />
                                    <span>Click to upload Artisan Photo</span>
                                </div>
                            )}
                        </div>

                        <button type="submit" className="adm-gal-submit">Upload to Gallery</button>
                    </form>
                </div>

                {/* --- VIEW / MANAGE SECTION --- */}
                <div className="adm-gal-card list-card">
                    <div className="adm-gal-card-head">
                        <CheckCircle size={20} color="#C5A059" />
                        <h3>Current Collection ({images.length})</h3>
                    </div>
                    
                    <div className="adm-gal-table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Preview</th>
                                    <th>Description</th>
                                    <th>Layout</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {images.map((img) => (
                                    <tr key={img._id}>
                                        <td>
                                            <div className="adm-gal-thumb">
                                                <img src={`${API_URL}/${img.image}`} alt="thumb" />
                                            </div>
                                        </td>
                                        <td><span className="adm-gal-desc">{img.alt}</span></td>
                                        <td><span className={`layout-tag ${img.layout}`}>{img.layout}</span></td>
                                        <td>
                                            <button className="adm-gal-del" onClick={() => handleDelete(img._id)}>
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {images.length === 0 && !isLoading && (
                            <div className="empty-state">No images in gallery yet.</div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminGallery;